import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Activity, Building2, Users, LogOut, ChevronDown, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, type BuildInfo } from '../api';
import { applyTheme, readTheme, type Theme } from '../lib/theme';
import { signOut } from '../auth-client';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export interface ShellTenant {
  id: string;
  name: string;
  slug: string;
}

const NAV = [
  { to: '/', label: 'Runs', icon: Activity },
  { to: '/tenants', label: 'Tenants', icon: Building2 },
  { to: '/users', label: 'Users', icon: Users, adminOnly: true },
];

/**
 * Rail + scope bar + content: the shape every operations console uses, because
 * an operator should never spend attention on finding their way around. The
 * novelty budget goes on the runs themselves.
 */
function ThemeSwitch() {
  const [theme, setTheme] = useState<Theme>('system');
  useEffect(() => setTheme(readTheme()), []);

  const options: { value: Theme; icon: typeof Monitor; label: string }[] = [
    { value: 'system', icon: Monitor, label: 'Match the system' },
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'dark', icon: Moon, label: 'Dark' },
  ];

  return (
    <div
      className="flex rounded-[5px] border border-rule bg-raised p-0.5"
      role="group"
      aria-label="Colour theme"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={theme === option.value}
          title={option.label}
          onClick={() => {
            applyTheme(option.value);
            setTheme(option.value);
          }}
          className={`flex flex-1 cursor-pointer items-center justify-center rounded-[3px] py-1 transition-colors duration-150 ${
            theme === option.value
              ? 'bg-panel text-ink shadow-[var(--shadow-panel)]'
              : 'text-ink-3 hover:text-ink'
          }`}
        >
          <option.icon className="size-3.5" aria-hidden />
          <span className="sr-only">{option.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * What is actually running, in the corner where you look when a change does not
 * seem to have landed. A short sha when the image was built from one, and the
 * build time either way.
 */
function RunningBuild() {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<BuildInfo>('/api/health'),
    refetchInterval: 60_000,
    retry: false,
  });
  const info = health.data;
  // An older api answers without a build; say nothing rather than "build ".
  if (!info?.version) return null;

  const short = /^[0-9a-f]{7,40}$/i.test(info.version) ? info.version.slice(0, 7) : info.version;
  const built = info.builtAt ? new Date(info.builtAt) : null;
  return (
    <p
      className="data mt-2 truncate text-ink-3"
      title={`Build ${info.version}${built ? `, built ${built.toLocaleString()}` : ''} · up ${Math.round(info.uptimeSeconds / 60)}m`}
    >
      build {short}
      {built ? ` · ${built.toLocaleDateString([], { month: '2-digit', day: '2-digit' })}` : ''}
    </p>
  );
}

function Wordmark() {
  return (
    <span className="flex items-center gap-2 text-[15px] font-semibold tracking-[-0.01em]">
      <span
        aria-hidden
        className="grid size-6 place-items-center rounded-[5px] bg-brand text-[11px] font-bold text-brand-ink"
      >
        tp
      </span>
      taskpilot
    </span>
  );
}

export function AppShell({
  email,
  isAdmin,
  tenants,
  tenant,
  onTenant,
  children,
}: {
  email: string;
  isAdmin: boolean;
  tenants: ShellTenant[];
  tenant: string | null;
  onTenant: (id: string | null) => void;
  children: ReactNode;
}) {
  const active = tenants.find((entry) => entry.id === tenant);
  const items = NAV.filter((item) => !item.adminOnly || isAdmin);
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[13.5rem_1fr]">
      <aside className="flex flex-col gap-5 border-b border-rule bg-rail px-3 py-3 md:sticky md:top-0 md:h-dvh md:border-r md:border-b-0 md:px-3 md:py-4">
        <div className="flex items-center justify-between gap-2 px-1">
          <Wordmark />
        </div>

        <nav className="flex gap-1 md:flex-col" aria-label="Sections">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-[5px] px-2.5 py-1.5 text-[13px] transition-colors duration-150 ${
                  isActive
                    ? 'bg-brand-soft font-medium text-brand'
                    : 'text-ink-2 hover:bg-raised hover:text-ink'
                }`
              }
            >
              <item.icon className="size-4" aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* On a phone this lives in the header menu instead of eating the fold. */}
        <div className="hidden md:mt-auto md:block">
          <ThemeSwitch />
          <div className="mt-3 rounded-[5px] border border-rule bg-panel px-2.5 py-2">
            <p className="label-caps">Signed in</p>
            <NavLink
              to="/account"
              className="data mt-0.5 block truncate text-ink-2 hover:text-brand hover:underline"
              title="Your account"
            >
              {email}
            </NavLink>
            <div className="mt-1.5 -ml-1.5 flex flex-wrap items-center gap-1">
              <Button variant="ghost" size="xs" className="gap-1.5" onClick={() => navigate('/account')}>
                <UserRound className="size-3" aria-hidden /> Account
              </Button>
              <Button variant="ghost" size="xs" className="gap-1.5" onClick={() => signOut()}>
                <LogOut className="size-3" aria-hidden /> Sign out
              </Button>
            </div>
            <RunningBuild />
          </div>
        </div>
      </aside>

      <div className="min-w-0">
        <header className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-rule bg-paper/85 px-4 py-2 backdrop-blur-sm lg:px-6">
          {tenants.length > 0 && (
            <div className="flex min-w-0 items-center gap-2">
              <span className="label-caps hidden sm:inline">Scope</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="max-w-[14rem] gap-2">
                    <span className="truncate">{active ? active.name : 'All tenants'}</span>
                    <ChevronDown className="size-3.5 text-ink-3" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60">
                  <DropdownMenuLabel>Work in</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onTenant(null)}>All tenants</DropdownMenuItem>
                  {tenants.map((entry) => (
                    <DropdownMenuItem key={entry.id} onClick={() => onTenant(entry.id)}>
                      {entry.name}
                      <span className="data ml-auto text-ink-3">{entry.slug}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {active && (
                <span className="data hidden text-ink-3 sm:inline" title="Tenant slug">
                  {active.slug}
                </span>
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="outline" size="sm" className="gap-2">
                  <UserRound className="size-3.5" aria-hidden />
                  Account
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="data">{email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/account')}>
                  <UserRound className="size-3.5" aria-hidden /> Account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => applyTheme('system')}>
                  <Monitor className="size-3.5" aria-hidden /> Match the system
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyTheme('light')}>
                  <Sun className="size-3.5" aria-hidden /> Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyTheme('dark')}>
                  <Moon className="size-3.5" aria-hidden /> Dark
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>
                  <LogOut className="size-3.5" aria-hidden /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto max-w-[92rem] px-4 py-5 pb-20 lg:px-6">{children}</main>
      </div>
    </div>
  );
}
